import { Pagination } from '@/domain/models'
import { Account } from '@/domain/usecases'
import { AccountDependentSearch } from '@/domain/usecases/account/company-manager/account-dependent-search'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import { parseNumber } from '@/main/helpers/number-helper'
import CompanyManagerBaseLoader from '@/main/routes/loaders/company-manager-base-loader'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import { LoaderFunctionArgs } from 'react-router-dom'

export type CompanyManagerDependentSearchLoaderType = {
  data: AccountDependentSearch.Model
  searchCriteria: AccountDependentSearch.RequestModel
  remote: AccountDependentSearch
  notFoundImage: HTMLImageElement
}

export default class CompanyManagerDependentSearchLoader extends CompanyManagerBaseLoader {
  constructor(
    readonly account: Account.Model,
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined
  ) {
    super(account, undefined, queryClient, ldClient, FeatureFlag.Key.COMPANIES_ON_DEPENDENT_ROLLOUT)
  }

  async loader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url)
    const searchCriteria = this.createCriteria(url.searchParams)

    return this.defer({
      data: this.getLoaderPromise(searchCriteria)
    })
  }

  private async getLoaderPromise(searchCriteria: AccountDependentSearch.RequestModel) {
    return Promise.all([
      this.promiseDependentSearch(searchCriteria),
      super.decodeImportImage(import('@images/dependent.svg'))
    ]).then(([{ data, remote }, notFoundImage]) => {
      return {
        data,
        searchCriteria,
        remote,
        notFoundImage
      } as CompanyManagerDependentSearchLoaderType
    })
  }

  private async promiseDependentSearch(searchCriteria: AccountDependentSearch.RequestModel) {
    return import(
      '@remote/account/company-manager/remote-company-manager-account-dependent-search-factory'
    )
      .then(module => module.makeRemoteCompanyManagerAccountDependentSearch)
      .then(async maker => {
        const remote = maker(this.queryClient)
        return {
          data: await remote.loader(searchCriteria),
          remote
        }
      })
  }

  private createCriteria(searchParams: URLSearchParams): AccountDependentSearch.RequestModel {
    const name = searchParams.get(AccountDependentSearch.NAME_PARAM_NAME)
    const email = searchParams.get(AccountDependentSearch.EMAIL_PARAM_NAME)
    const page = searchParams.get(Pagination.PAGE_PARAM_NAME)
    const pageSize = searchParams.get(Pagination.PAGE_SIZE_PARAM_NAME)
    return {
      name,
      email,
      page: parseNumber(page, Pagination.DEFAULT_PAGE)!,
      pageSize: parseNumber(pageSize, AccountDependentSearch.DEFAULT_PAGE_SIZE)
    }
  }
}
